import { useQuery } from '@tanstack/react-query';
import { mcpUsageApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { McpUsageQueryParams, McpUsageTimeseriesParams } from '../types/api.js';

/**
 * scope/email/team の整合性が取れていない (scope=team で team 未選択, scope=member で email 未選択)
 * 状態のクエリは backend が refine で 400 を返すため、フロント側で即時無効化する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business mcp-usage クエリの有効性判定
 * @graph-connects none
 */
function isMcpUsageParamsValid(params: McpUsageQueryParams | undefined): boolean {
  if (!params) return true;
  if (params.scope === 'team') return Boolean(params.team);
  if (params.scope === 'member') return Boolean(params.email);
  return true;
}

/**
 * MCP 利用量サマリ。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business mcp-usage summary fetch hook
 * @graph-connects /mcp-usage/summary [calls] mcp-usage サマリ取得
 */
export function useMcpUsageSummary(params: McpUsageQueryParams) {
  return useQuery({
    queryKey: queryKeys.mcpUsage.summary(params),
    queryFn: () => mcpUsageApi.getSummary(params),
    enabled: isMcpUsageParamsValid(params),
  });
}

/**
 * MCP 利用量の時系列推移。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business mcp-usage timeseries fetch hook
 * @graph-connects /mcp-usage/timeseries [calls] mcp-usage 時系列取得
 */
export function useMcpUsageTimeseries(params: McpUsageTimeseriesParams) {
  return useQuery({
    queryKey: queryKeys.mcpUsage.timeseries(params),
    queryFn: () => mcpUsageApi.getTimeseries(params),
    enabled: isMcpUsageParamsValid(params),
  });
}

/**
 * MCP 利用量の server 別 breakdown。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business mcp-usage by server fetch hook
 * @graph-connects /mcp-usage/by-server [calls] mcp-usage server 別 breakdown 取得
 */
export function useMcpUsageByServer(params: McpUsageQueryParams) {
  return useQuery({
    queryKey: queryKeys.mcpUsage.byServer(params),
    queryFn: () => mcpUsageApi.getByServer(params),
    enabled: isMcpUsageParamsValid(params),
  });
}

/**
 * MCP 利用量の server × tool 別 breakdown。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business mcp-usage by tool fetch hook
 * @graph-connects /mcp-usage/by-tool [calls] mcp-usage tool 別 breakdown 取得
 */
export function useMcpUsageByTool(params: McpUsageQueryParams) {
  return useQuery({
    queryKey: queryKeys.mcpUsage.byTool(params),
    queryFn: () => mcpUsageApi.getByTool(params),
    enabled: isMcpUsageParamsValid(params),
  });
}

/**
 * MCP 利用量の user 別 breakdown。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business mcp-usage by user fetch hook
 * @graph-connects /mcp-usage/by-user [calls] mcp-usage user 別 breakdown 取得
 */
export function useMcpUsageByUser(params: McpUsageQueryParams) {
  return useQuery({
    queryKey: queryKeys.mcpUsage.byUser(params),
    queryFn: () => mcpUsageApi.getByUser(params),
    enabled: isMcpUsageParamsValid(params),
  });
}

/**
 * MCP 利用量集計対象チーム一覧。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business mcp-usage teams fetch hook
 * @graph-connects /mcp-usage/teams [calls] mcp-usage teams 取得
 */
export function useMcpUsageTeams(params: Pick<McpUsageQueryParams, 'range' | 'from' | 'to'>) {
  return useQuery({
    queryKey: queryKeys.mcpUsage.teams(params),
    queryFn: () => mcpUsageApi.getTeams(params),
  });
}
